import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { List, Map } from 'immutable';

import config from '../../../../../config';
import { unwrap } from '../../../../../utils/ChangeSpy';
import Table from '../../../../helpers/AgGridTable';
import { ACTION_DELETE } from '../../../../helpers/DeleteItem';
import getPercent from '../../../../../utils/getPercent';
import DateEditor from '../../DateEditor';
import TextEditor from '../../TextEditor';
import { GeneralFormat } from '../../GeneralFormat';
import AgHeader from './AgHeader';
import { GridCard } from '../../../../helpers/GridCard';

import style from './style.module.css';

/**
 * Return sum by field.
 *
 * @param {List} approvals Immutable List of approvals.
 * @param {string} field Field name.
 */
const getSum = (approvals, field) => approvals.reduce((sum, approval) => sum + approval.get(field), 0);

/**
 * Return total value.
 *
 * @param {List} approvals List of approved.
 * @param {string} numberOf Field name.
 * @param {number} percentOf Sum of approved.
 * @returns {string}
 */
const getTotalValue = (approvals, numberOf, percentOf) => {
  const sum = getSum(approvals, numberOf);
  const percent = getPercent(sum, percentOf);

  return `${sum} (${percent}%)`;
};

/**
 * Return total count of approved and percents of  ex.:1 (100%).
 *
 * @param {number} approved Count of approved.
 * @param {number} targets Count of targets.
 * @returns {string}
 */
const getTotalApprovedValue = (approved, targets) => {
  const percent = getPercent(approved, targets);

  return `${approved} (${percent}%)`;
};

/**
 * Return an array with an object which contains total stats data.
 *
 * @param {List} approvals Immutable List of approvals.
 */
export const getTotalStats = approvals => {
  const approvalsReturned = approvals.filter(approval => approval.getIn(['dateReceived', 'value'], 0) !== null);
  const sumReturnedTargets = getSum(approvalsReturned, 'numberTargets');
  const sumApproved = getSum(approvalsReturned, 'numberApproved');
  const sumTotal = getSum(approvals, 'numberApproved');
  const sumTargets = getSum(approvals, 'numberTargets');
  const totalNumberApproved = getSum(approvals, 'numberApproved');

  return [
    {
      applistLabel: { value: 'Total (Returned Approval Lists):' },
      dateSent: '',
      dateReceived: '',
      numberTargets: getSum(approvalsReturned, 'numberTargets'),
      numberApproved: getTotalValue(approvalsReturned, 'numberApproved', sumReturnedTargets),
      numberOfContacted: getTotalValue(approvalsReturned, 'numberOfContacted', sumApproved),
      numberOfLeads: getTotalValue(approvalsReturned, 'numberOfLeads', sumApproved),
      numberOfCc: getTotalValue(approvalsReturned, 'numberOfCc', sumApproved),
      numberOfVisit: getTotalValue(approvalsReturned, 'numberOfVisit', sumApproved),
      numberOfOffers: getTotalValue(approvalsReturned, 'numberOfOffers', sumApproved),
      numberOfLois: getTotalValue(approvalsReturned, 'numberOfLois', sumApproved),
      numberOfClosed: getTotalValue(approvalsReturned, 'numberOfClosed', sumApproved),
    },
    {
      applistLabel: { value: 'Total (All):' },
      dateSent: '',
      dateReceived: '',
      numberTargets: sumTargets,
      numberApproved: getTotalApprovedValue(totalNumberApproved, sumTargets),
      numberOfContacted: getTotalValue(approvals, 'numberOfContacted', sumTotal),
      numberOfLeads: getTotalValue(approvals, 'numberOfLeads', sumTotal),
      numberOfCc: getTotalValue(approvals, 'numberOfCc', sumTotal),
      numberOfVisit: getTotalValue(approvals, 'numberOfVisit', sumTotal),
      numberOfOffers: getTotalValue(approvals, 'numberOfOffers', sumTotal),
      numberOfLois: getTotalValue(approvals, 'numberOfLois', sumTotal),
      numberOfClosed: getTotalValue(approvals, 'numberOfClosed', sumTotal),
    },
  ];
};

/**
 * Approval list component.
 *
 * @param props {Object}.
 * @param approvals {Immutable.List} Approval list.
 * @param common {Immutable.Map} Common object.
 * @param onChange {Function} Handle event of switching a row into edit mode.
 * @param onSort {Function} Handle event of sorting a column.
 * @class
 */
const Approvals = ({ approvals, common, onSort, onChange, onRowInsert, onDelete, onRowDoubleClick }) => {
  const gridApi = useRef(null);
  const cellClickToEdit = useRef(false);

  const checkEditable = useCallback(
    params => params.node.data.id !== 'no_approval_lists' && cellClickToEdit.current,
    [],
  );

  const handleInsert = useCallback(() => {
    if (!gridApi.current) return;

    const editingCells = gridApi.current.getEditingCells();

    if (editingCells && editingCells.length > 0) {
      return;
    }

    gridApi.current.stopEditing();

    const newRow = {
      id: 0,
      applistLabel: { value: '' },
      dateReceived: { value: null },
      dateSent: { value: null },
      numberApproved: 0,
      numberTargets: 0,
    };

    const count = gridApi.current.getModel().getRowCount();
    const transaction = gridApi.current.applyTransaction({
      add: [newRow],
      addIndex: count - 1,
    });

    const newIndex = transaction.add[0].rowIndex;

    cellClickToEdit.current = true;
    gridApi.current.startEditingCell({
      rowIndex: newIndex,
      colKey: 'applistLabel',
    });
  }, []);

  const columns = useMemo(
    () =>
      config.tables.getIn(['project', 'approval', 'columns']).map(column => {
        const field = column.get('field');

        if (['dateReceived', 'dateSent'].includes(field)) {
          return column.merge({
            editable: checkEditable,
            cellRendererFramework: GeneralFormat,
            headerComponentFramework: AgHeader,
            sortable: true,
            cellEditorFramework: DateEditor,
          });
        }

        if (field === 'applistLabel') {
          return column.merge({
            editable: checkEditable,
            cellEditorFramework: TextEditor,
            headerComponentFramework: AgHeader,
            onClick: handleInsert,
            sortable: true,
            cellRendererFramework: GeneralFormat,
            colSpan: params => (params.node.rowPinned ? 3 : 1),
          });
        }

        if (field.startsWith('numberOf')) {
          const headerTooltip = {
            numberApproved: 'Approved% = #Approved / #Targets',
            numberOfContacted: 'Contacted% = #Contacted / #Approved',
            numberOfLeads: 'Leads% = #Leads / #Approved',
            numberOfCc: 'CC% = #CC / #Approved',
            numberOfVisit: 'Visit% = #Visit / #Approved',
            numberOfOffers: 'Offers A% = #Offers / #Approved',
            numberOfLois: 'LOIs% = #LOIs / #Approved',
            numberOfClosed: 'Closed% = #Closed / #Approved',
          }[field];

          return column.merge({
            headerComponentFramework: AgHeader,
            cellRendererFramework: GeneralFormat,
            headerTooltip,
          });
        }

        return column.merge({
          headerComponentFramework: AgHeader,
          sortable: true,
          cellRendererFramework: GeneralFormat,
        });
      }),
    [checkEditable, handleInsert],
  );

  const preventEditing = useCallback(() => {
    cellClickToEdit.current = false;
  }, []);

  const handleCellClicked = useCallback(
    event => {
      const {
        column: { colId },
        node: { rowIndex },
      } = event;

      if (common.get('approvalLoading')) return;

      if (['applistLabel', 'dateReceived', 'dateSent'].includes(colId)) {
        cellClickToEdit.current = true;
        gridApi.current.startEditingCell({
          rowIndex,
          colKey: colId,
        });
      } else {
        gridApi.current.stopEditing();
      }
    },
    [common],
  );

  const handleRowValueChanged = useCallback(
    ({ data }) => {
      if (data.id > 0 || data.mode === 'edit') {
        onChange({
          type: 'approvals',
          rowIndex: data.index,
          field: 'applistLabel',
          value: data.applistLabel.value,
        });
        onChange({
          type: 'approvals',
          rowIndex: data.index,
          field: 'dateReceived',
          value: data.dateReceived.value,
        });
        onChange({
          type: 'approvals',
          rowIndex: data.index,
          field: 'dateSent',
          value: data.dateSent.value,
        });
      } else if (data.id === 0 && (data.applistLabel.value || data.dateReceived.value || data.dateSent.value)) {
        onRowInsert({
          applistLabel: data.applistLabel.value,
          dateReceived: data.dateReceived.value,
          dateSent: data.dateSent.value,
          projectId: data.projectId,
        });
      }
    },
    [onChange, onRowInsert],
  );

  const mapApprovalsData = useCallback(
    () =>
      approvals.map(approval => {
        const percentApproved = `${approval.get('percentApproved')}%`;
        const numberApproved = approval.get('numberApproved');
        const numberOfCc = approval.get('numberOfCc');
        const numberOfVisit = approval.get('numberOfVisit');
        const numberOfOffers = approval.get('numberOfOffers');
        const numberOfLois = approval.get('numberOfLois');
        const numberOfClosed = approval.get('numberOfClosed');

        return approval
          .set('numberApproved', `${numberApproved} (${percentApproved})`)
          .set(
            'numberOfContacted',
            `${approval.get('numberOfContacted')} (${getPercent(approval.get('numberOfContacted'), numberApproved)}%)`,
          )
          .set(
            'numberOfLeads',
            `${approval.get('numberOfLeads')} (${getPercent(approval.get('numberOfLeads'), numberApproved)}%)`,
          )
          .set('numberOfCc', `${numberOfCc} (${getPercent(numberOfCc, numberApproved)}%)`)
          .set('numberOfVisit', `${numberOfVisit} (${getPercent(numberOfVisit, numberApproved)}%)`)
          .set('numberOfOffers', `${numberOfOffers} (${getPercent(numberOfOffers, numberApproved)}%)`)
          .set('numberOfLois', `${numberOfLois} (${getPercent(numberOfLois, numberApproved)}%)`)
          .set('numberOfClosed', `${numberOfClosed} (${getPercent(numberOfClosed, numberApproved)}%)`);
      }),
    [approvals],
  );

  useEffect(() => {
    if (gridApi.current) {
      gridApi.current.setPinnedBottomRowData(getTotalStats(approvals));
      gridApi.current.sizeColumnsToFit();
      gridApi.current.refreshCells({ force: true });
    }

    if (approvals.size && !unwrap(approvals.getIn([approvals.size - 1, 'id'])) && gridApi.current) {
      const rowIndex = approvals.size - 1;

      gridApi.current.startEditingCell({ rowIndex, colKey: 'applistLabel' });
      gridApi.current.setFocusedCell(rowIndex, 'applistLabel');
      gridApi.current.ensureIndexVisible(rowIndex);
    }
    cellClickToEdit.current = false;
  }, [approvals, common]);

  const handleContextMenuClick = (event, data) => {
    const { id, numberTargets } = data.rowData;

    if (id !== 'no_approval_lists') {
      onDelete(event, id, numberTargets);
    }
  };

  return (
    <div className={style.cell}>
      <GridCard className={classNames(style.grid, style.approvalGrid, 'full-height')}>
        <Table
          columnDefs={columns}
          contextActionList={ACTION_DELETE}
          editType="fullRow"
          getRowNodeId={data => data.index}
          isFetching={common.get('approvalLoading')}
          onCellClicked={handleCellClicked}
          onContextMenuClick={handleContextMenuClick}
          onGridReady={params => {
            gridApi.current = params.api;
            gridApi.current.addEventListener('rowEditingStarted', preventEditing);
          }}
          onRowDoubleClicked={params => {
            if (params.data.id > 0) {
              onRowDoubleClick({
                row: Map({
                  id: params.data.id,
                  projectId: params.data.projectId,
                }),
                type: 'approvals',
              });
            } else if (params.data.id === 'no_approval_lists') {
              onRowDoubleClick({
                row: Map({
                  id: 'no_approval_lists',
                  projectId: params.data.projectId,
                }),
                type: 'approvals',
              });
            }
          }}
          onRowValueChanged={handleRowValueChanged}
          onSortModelChanged={sortModel => {
            if (gridApi.current) {
              gridApi.current.stopEditing(true);
            }

            if (sortModel && sortModel.length) {
              onSort(sortModel, {
                type: 'approvals',
                direction: sortModel[0].sort === 'asc' ? 'down' : 'up',
                field: sortModel[0].colId,
              });
            }
          }}
          rowData={mapApprovalsData()}
          sortable
        />
      </GridCard>
    </div>
  );
};

Approvals.propTypes = {
  approvals: PropTypes.instanceOf(List).isRequired,
  common: PropTypes.instanceOf(Map).isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRowDoubleClick: PropTypes.func.isRequired,
  onRowInsert: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
};

export default Approvals;
